/* eslint-disable */
import { mapActions, mapGetters, mapState } from 'vuex';
import moment from 'moment';
import Contract from '@/models/contract';
import marketChangePrice from '@/components/market/market-change-price/market-change-price.vue';
import marketSharesBar from '@/components/market/market-shares-bar/market-shares-bar.vue';
import buttonPrice from '@/components/market/v2/button-price/button-price.vue';
import { contractOwnershipNum, isMarketSingle, isMarketSettled, isMarketDisabled, isMarketJourneyDisabled } from '@/utils/helpers';
import { waitForFirebaseInitialized, waitForStatusReady } from '@/utils/mutex';
import accountVerifyMixin from '@/mixins/accountVerify';
import scrollTo from '@/utils/scrollTo';
import marketDisabledOutcomeV2
  from '@/components/market/v2/market-disabled-outcome-v2/market-disabled-outcome-v2.vue';
import marketTradingUnavailableV2 from '@/components/market/v2/market-trading-unavailable-v2/market-trading-unavailable-v2.vue';

export const thousandDisplay = (value) => {
  if (value >= 1.0e+4) {
    return `${Math.round(value / 1.0e+3)}K`;
  }
  return (value >= 1.0e+3) ? `${Math.floor((10 * value) / 1.0e+3) / 10}K` : value;
};

export const TITLE_LATEST_PRICE = 'Latest Price';
const firstNonEmptyString = (...values) => values.find(
  (value) => typeof value === 'string' && value.trim() !== '',
);
export const OPERATIONS = {
  BUY: 'buy',
  SELL: 'sell',
};
export const VALUES = {
  YES: 'yes',
  NO: 'no',
};
export const BUTTONS = {
  BUY_NO: {
    id: 'buy-no',
    type: OPERATIONS.BUY,
    value: VALUES.NO,
  },
  BUY_YES: {
    id: 'buy-yes',
    type: OPERATIONS.BUY,
    value: VALUES.YES,
  },
  SELL_NO: {
    id: 'sell-no',
    type: OPERATIONS.SELL,
    value: VALUES.NO,
  },
  SELL_YES: {
    id: 'sell-yes',
    type: OPERATIONS.SELL,
    value: VALUES.YES,
  },
};

export function getActiveButtonAfterSwitch(contractModel, switchTo) {
  let button = null;

  if (switchTo === 'left') {
    button = BUTTONS.BUY_YES;;
  } else if (switchTo === 'right') {
    button = BUTTONS.BUY_NO;
  }

  return button;
}


export default {
  mixins: [
    accountVerifyMixin,
  ],
  components: {
    buttonPrice,
    marketChangePrice,
    marketSharesBar,
    marketDisabledOutcomeV2,
    marketTradingUnavailableV2,
  },
  props: {
    market: {
      type: Object,
      required: true,
    },
    contract: {
      type: Object,
      required: true,
    },
    forCmsWidget: {
      type: Boolean,
      default: false,
    },
    forceMobileLayout: {
      type: Boolean,
      default: false,
    },
    dashboardMode: {
      type: Boolean,
      default: false,
    },
    dontShowOdds: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hovered: false,
      BUTTONS,
      OPERATIONS,
      VALUES,
      isWaiting: false,
      activeButton: null,
      chart: null,
    };
  },
  mounted() {
    this.initializePurchaseButtons();
  },
  computed: {
    ...mapGetters('user', ['isLoggedIn']),
    ...mapGetters('app', ['isLayoutMobile', 'isLayoutDesktop', 'isTradingEnabled']),
    ...mapState('trade', ['tradeOptions', 'tradeOperationToggle', 'isInConfirmationMode']),
    ...mapState('user', ['status']),
    ...mapState('app', ['showOdds']),
    useMobileLayout() {
      return this.forceMobileLayout || this.isLayoutMobile;
    },
    useDesktopLayout() {
      return !this.useMobileLayout;
    },
    contractModel() {
      return new Contract(this.contract);
    },
    isMarketSingle() {
      return isMarketSingle(this.market);
    },
    isMarketJourneyDisabled() {
      return isMarketJourneyDisabled(this.market);
    },
    singleMarketTitle() {
      return firstNonEmptyString(
        this.market && this.market.marketShortName,
        this.market && this.market.marketName,
        this.contractModel.marketName,
        this.contractModel.contractName,
      ) || TITLE_LATEST_PRICE;
    },
    cmsWidgetTitle() {
      return firstNonEmptyString(
        this.contract && this.contract.contractName,
      ) || TITLE_LATEST_PRICE;
    },
    title() {
      if (this.forCmsWidget) {
        return this.cmsWidgetTitle;
      }

      return this.isMarketSingle ?
        this.singleMarketTitle :
        firstNonEmptyString(
          this.contractModel.contractName,
          this.contractModel.name,
        );
    },
    classModifiers() {
      return {
        'market-contract-horizontal-sushi': true,
        'market-contract-horizontal-sushi--dashboard': this.dashboardMode,
        'market-contract-horizontal-sushi--hovered': this.hovered,
        'market-contract-horizontal-sushi--forced-mobile': this.forceMobileLayout,
        'market-contract-horizontal-sushi--no-shares-or-offers': !this.showOffersOrShares,
        'market-contract-horizontal-sushi--settled-yes': this.isMarketSettled && this.isPayoutTypeYes,
        'market-contract-horizontal-sushi--settled-no': this.isMarketSettled && this.isPayoutTypeNo,
        'market-contract-horizontal-sushi--should-close': this.shouldClosePurchaseJourney,
      };
    },
    imageUrl() {
      return this.contractModel.contractImageSmallUrl || this.contractModel.contractImageUrl;
    },
    contractImageStyleModifiers() {
      return {
        'background-image': `url(${this.imageUrl})`,
      };
    },
    openOffersText() {
      const buyOffers = thousandDisplay(this.contractModel.userOpenOrdersBuyQuantity, false);
      const sellOffers = thousandDisplay(this.contractModel.userOpenOrdersSellQuantity, false);
      // const prefix = (this.useDesktopLayout && !this.isLayoutDashboard) ? 'Offers: ' : '';
      return `${buyOffers}/${sellOffers}`;
    },
    showOpenOffers() {
      return !this.isMarketSettled && this.contractModel.userOpenOrders > 0;
    },
    showSharesBar() {
      return !this.isMarketSettled && this.contractModel.userQuantity > 0;
    },
    sharesOwnedStyleModifiers () {
      return {
        'market-contract-horizontal-sushi__image-shares-owned': true,
        'market-contract-horizontal-sushi__image-shares-owned--no': this.contractModel.mode === 'no',
      }
    },
    showOffersOrShares() {
      return this.showSharesBar || this.showOpenOffers;
    },

    isMarketDisabled() {
      return isMarketDisabled(this.market);
    },
    isMarketSettled() {
      return isMarketSettled(this.market);
    },
    isPayoutTypeYes() {
      return this.contract.payoutType === 1;
    },
    isPayoutTypeNo() {
      return this.isMarketSingle && this.contract.payoutType === 0;
    },
    shouldClosePurchaseJourney() {
      return this.tradeOptions &&
        this.tradeOptions.contractId === this.contract.contractId;
    },
    currentPriceSymbol() {
      if (this.dontShowOdds) return '&cent;';
      return this.showOdds ? '%' : '&cent;';
    },
    currentPrice() {
      return this.contractModel.lastTradePriceAsCents;
    },
  },
  watch: {
    // opposite side of order book was clicked -- switching sides
    tradeOperationToggle(switchTo) {
      if (this.contract.contractId !== this.tradeOptions.contractId || !switchTo) return;
      const button = getActiveButtonAfterSwitch(this.contractModel, switchTo);
      this.toggleTradeOperation(null);
      this.openPurchaseJourney(this.contractModel.contractId, button.type, button.value, button.id);
    },
  },
  methods: {
    ...mapActions('trade', ['setTradeOptions', 'toggleTradeOperation']),
    ...mapActions('popup', ['setCurrentPopup']),
    ...mapActions('market', ['pushSessionHistoryContract']),
    findActiveButton() {
      return Object.values(BUTTONS).find(i => i.id === this.activeButton);
    },
    getTypeAndValueIfButtonActiveOrDefaults() {
      let type = this.tradeOptions.operationType;
      let value = this.tradeOptions.operationValue;
      if (this.activeButton) {
        const button = this.findActiveButton();
        type = button.type;
        value = button.value;
      }
      return { type, value };
    },
    onClickSharesBar() {
      const { type, value } = this.getTypeAndValueIfButtonActiveOrDefaults();
      this.openPurchaseJourney(
        this.contractModel.contractId,
        type,
        value);
    },
    onClickOpenOffers() {
      let tab = 'sell-offers';
      const operationType = this.tradeOptions.operationType || '';
      if (
        (operationType === '' && this.contractModel.userOpenOrdersBuyQuantity) ||
        (operationType === OPERATIONS.BUY && this.contractModel.userOpenOrdersBuyQuantity)
      ) {
        tab = 'buy-offers';
      }
      const { type, value } = this.getTypeAndValueIfButtonActiveOrDefaults();
      this.openPurchaseJourney(
        this.contractModel.contractId,
        type,
        value);
    },
    initializePurchaseButtons() {
      let button = BUTTONS.BUY_YES;
      if (this.contractModel.mode === VALUES.NO) {
        button = (this.contract.contractId === this.tradeOptions.contractId && this.tradeOptions.operationValue === 'no') ? BUTTONS.BUY_NO : BUTTONS.BUY_YES;
      } else if (this.contractModel.mode === VALUES.YES) {
        button = BUTTONS.BUY_YES;
      }
      this.activeButton = button.id;

      if (
        (!this.isMarketSingle || this.useMobileLayout) &&
        this.tradeOptions &&
        (this.contract.contractId === this.tradeOptions.contractId) &&
        (
          button.type !== this.tradeOptions.operationType
          || button.value !== this.tradeOptions.operationValue
        )
      ) {
        this.openPurchaseJourney(
          this.contractModel.contractId, button.type, button.value);
      }
    },
    onMouseOver() {
      this.hovered = true;
    },
    onMouseLeave() {
      this.hovered = false;
    },
    onContractClick() {
      if (this.shouldClosePurchaseJourney) {
        this.setTradeOptions(null);
        return;
      }
      if (this.activeButton) {
        let button = BUTTONS.BUY_YES;
        if (this.contractModel.mode) {
          button = (this.contractModel.mode === VALUES.NO) ? BUTTONS.BUY_NO : BUTTONS.BUY_YES;
        }
        this.openPurchaseJourney(
          this.contractModel.contractId, button.type, button.value, button.id);

        // const $el = this.$refs[this.activeButton];
        // if (!$el) return;
        // const ev = new MouseEvent('click');
        // $el.dispatchEvent(ev);
      } else {
        const button = (this.contractModel.mode === VALUES.NO) ? BUTTONS.BUY_NO : BUTTONS.BUY_YES;
        this.openPurchaseJourney(
          this.contractModel.contractId, button.type, button.value);
      }
    },
    isButtonActive(index) {
      const isOn = this.activeButton === index;
      const isButtonActive = (this.tradeOptions.contractId === this.contract.contractId);

      return {
        'market-contract-horizontal-sushi__button-single--active': isButtonActive,
        'market-contract-horizontal-sushi__button-single--on': isOn,
        'market-contract-horizontal-sushi__button-single--off': !isOn,
        'market-contract-horizontal-sushi__button-single--off-yes': (this.tradeOptions.operationValue === VALUES.YES),
        'market-contract-horizontal-sushi__button-single--off-no': (this.tradeOptions.operationValue === VALUES.NO),
      };
    },
    openPurchaseJourney(
      contractId, operationType, operationValue,
      activateButton = null,
    ) {
      if (this.isWaiting) {
        return;
      }

      this.isWaiting = true;
      waitForFirebaseInitialized()
        .then(() => {
        if (this.isLoggedIn) {
          return waitForStatusReady();
        }
        return Promise.resolve(1);
      })
        .then(() => {
          this.isWaiting = false;
          // contract does not modify tradeOptions, is simply used as a widget somewhere
          if (this.forCmsWidget) {
            this.$emit('contractClicked', { contractId });
            return;
          }

          if (this.isMarketJourneyDisabled || (this.isMarketDisabled && contractOwnershipNum(this.contractModel) === 0)) return;
          if (activateButton) {
            this.activeButton = activateButton;
          }
          this.pushSessionHistoryContract({
            value: {
              ...this.contract,
              marketName: this.market.marketShortName || this.market.marketName
            }
          });
          this.setTradeOptions({
            contractId,
            operationType,
            operationValue: operationValue !== null ?
              operationValue :
              (this.contractModel.mode || VALUES.YES),
          });
          if (this.dashboardMode) {
            setTimeout(() => {
              const $el = document.querySelector(`[data-contract-id="${contractId}"]`);
              if ($el === null) return;
              const destination = Math.max(0, $el.offsetTop);
              scrollTo(destination, 250, 'linear');
            }, 1000);
          }
        });
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/market/market-contract-horizontal-sushi/market-contract-horizontal-sushi.js